import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, CATEGORIES, formatApiError } from "@/lib/config";
import { useTiers, invalidateTiersCache } from "@/lib/useTiers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Users, BookOpen, CheckCircle2, DollarSign, Trash2, Plus, Upload, Film, CheckCircle, Pencil, Ban, ShieldCheck, FileText, Link as LinkIcon, Mail } from "lucide-react";
import ProtocolAdminTab from "@/pages/admin/ProtocolAdminTab";
import TiersAdminTab from "@/pages/admin/TiersAdminTab";
import EventsAdminTab from "@/pages/admin/EventsAdminTab";
import LessonResourcesAdmin from "@/pages/admin/LessonResourcesAdmin";
import SettingsAdminTab from "@/pages/admin/SettingsAdminTab";
import VimeoUploader from "@/pages/admin/VimeoUploader";
import BookingsAdminTab from "@/pages/admin/BookingsAdminTab";
import NewsletterAdminTab from "@/pages/admin/NewsletterAdminTab";
import AnalyticsAdminTab from "@/pages/admin/AnalyticsAdminTab";
import AccountabilityAdminTab from "@/pages/admin/AccountabilityAdminTab";
import IntakesAdminTab from "@/pages/admin/IntakesAdminTab";
import BroadcastAdminTab from "@/pages/admin/BroadcastAdminTab";
import PromoCodesAdminTab from "@/pages/admin/PromoCodesAdminTab";
import RequestIntakeModal from "@/pages/admin/RequestIntakeModal";

export default function AdminPanel() {
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [intakeRequestFor, setIntakeRequestFor] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [form, setForm] = useState(blankLesson());
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);
  const { tiers } = useTiers();

  const load = async () => {
    const [a, u, l] = await Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/users"),
      api.get("/lessons"),
    ]);
    setAnalytics(a.data); setUsers(u.data); setLessons(l.data);
  };

  useEffect(() => { load(); }, []);

  const createLesson = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        duration_min: Number(form.duration_min) || 10,
        order: Number(form.order) || 0,
        drip_day: Number(form.drip_day) || 0,
        key_takeaways: typeof form.key_takeaways === "string"
          ? form.key_takeaways.split("\n").filter(Boolean) : form.key_takeaways,
        action_steps: typeof form.action_steps === "string"
          ? form.action_steps.split("\n").filter(Boolean) : form.action_steps,
      };
      if (editingLessonId) {
        await api.patch(`/admin/lessons/${editingLessonId}`, payload);
        toast.success("Lesson updated.");
      } else {
        await api.post("/admin/lessons", payload);
        toast.success("Lesson published.");
      }
      setForm(blankLesson());
      setShowForm(false);
      setEditingLessonId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const startEditLesson = (lesson) => {
    setEditingLessonId(lesson.lesson_id);
    setForm({
      title: lesson.title || "",
      category: lesson.category || "peptides",
      tier_required: lesson.tier_required || "workshop",
      description: lesson.description || "",
      video_url: lesson.video_url || "",
      video_file_id: lesson.video_file_id || "",
      vimeo_video_id: lesson.vimeo_video_id || "",
      thumbnail_url: lesson.thumbnail_url || "",
      duration_min: lesson.duration_min || 15,
      order: lesson.order ?? 100,
      drip_day: lesson.drip_day ?? 0,
      key_takeaways: (lesson.key_takeaways || []).join("\n"),
      action_steps: (lesson.action_steps || []).join("\n"),
    });
    setShowForm(true);
    // Scroll the form into view
    setTimeout(() => {
      document.querySelector('[data-testid="lesson-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const cancelLessonEdit = () => {
    setEditingLessonId(null);
    setShowForm(false);
    setForm(blankLesson());
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    const okExts = ["mp4", "mov", "webm", "m4v"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!okExts.includes(ext)) {
      toast.error(`Unsupported file type .${ext}`);
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      toast.error("File too large. Max 500 MB.");
      return;
    }
    setUploading(true);
    setUploadPct(0);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/admin/upload/video", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
      });
      setForm((f) => ({
        ...f,
        video_file_id: data.file_id,
        video_url: "",
      }));
      toast.success(`Uploaded ${data.original_filename}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setUserTier = async (userId, tier) => {
    try {
      await api.patch(`/admin/users/${userId}/tier`, { tier });
      toast.success(`Tier updated to ${tier}`);
      setUsers((us) => us.map((u) => (u.user_id === userId ? { ...u, tier } : u)));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const toggleUserBlock = async (u) => {
    const nextStatus = (u.status === "blocked") ? "active" : "blocked";
    const verb = nextStatus === "blocked" ? "Block" : "Unblock";
    if (!window.confirm(`${verb} ${u.name} (${u.email})?`)) return;
    try {
      await api.patch(`/admin/users/${u.user_id}/status`, { status: nextStatus });
      setUsers((us) => us.map((x) => (x.user_id === u.user_id ? { ...x, status: nextStatus } : x)));
      toast.success(`${verb}ed ${u.name}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setUsers((us) => us.filter((x) => x.user_id !== u.user_id));
      toast.success(`Deleted ${u.name}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const deleteLesson = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      await api.delete(`/admin/lessons/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <AppShell>
      <div className="space-y-8">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Control Room</h1>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="bg-[#0A0A0A] border border-white/5 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="accountability" data-testid="tab-accountability">Accountability</TabsTrigger>
            <TabsTrigger value="intakes" data-testid="tab-intakes">Intakes</TabsTrigger>
            <TabsTrigger value="lessons" data-testid="tab-lessons">Lessons</TabsTrigger>
            <TabsTrigger value="protocol" data-testid="tab-protocol">Protocol</TabsTrigger>
            <TabsTrigger value="tiers" data-testid="tab-tiers">Tiers</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="broadcast" data-testid="tab-broadcast">Broadcast</TabsTrigger>
            <TabsTrigger value="promo" data-testid="tab-promo">Promo Codes</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
            {analytics && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Stat icon={Users} label="Users" value={analytics.total_users} />
                  <Stat icon={BookOpen} label="Lessons" value={analytics.total_lessons} />
                  <Stat icon={CheckCircle2} label="Completions" value={analytics.total_completions} />
                  <Stat icon={DollarSign} label="Revenue" value={`$${(analytics.total_revenue_cents / 100).toFixed(0)}`} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="glass rounded-2xl p-6">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mb-4">Users by Tier</div>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={Object.entries(analytics.tiers).map(([k,v]) => ({ tier: k, users: v }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                        <XAxis dataKey="tier" stroke="#888" fontSize={11} />
                        <YAxis stroke="#888" fontSize={11} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #333", borderRadius: 8 }} />
                        <Bar dataKey="users" radius={[6,6,0,0]}>
                          {Object.keys(analytics.tiers).map((k, i) => (
                            <Cell key={k} fill={["#444","#FF8A00","#FF6B1A","#E52E71","#FFB347"][i] || "#888"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="glass rounded-2xl p-6">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mb-4">Conversion Funnel</div>
                    <div className="space-y-3">
                      {Object.entries(analytics.conversion).map(([k, v]) => (
                        <div key={k}>
                          <div className="flex justify-between text-xs text-white/70 mb-1">
                            <span>{k.replace(/_/g, " → ")}</span>
                            <span>{v}</span>
                          </div>
                          <div className="h-2 bg-[#0F0F0F] rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"
                                 style={{ width: `${Math.min(100, (v / Math.max(analytics.total_users, 1)) * 100)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <AnalyticsAdminTab />
          </TabsContent>

          <TabsContent value="accountability" className="mt-6">
            <AccountabilityAdminTab />
          </TabsContent>

          <TabsContent value="intakes" className="mt-6">
            <IntakesAdminTab />
          </TabsContent>

          <TabsContent value="lessons" className="mt-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display text-2xl">Lessons ({lessons.length})</h2>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="backfill-thumbnails-btn"
                  onClick={async () => {
                    try {
                      const r = await api.post("/admin/lessons/backfill-thumbnails");
                      toast.success(`Thumbnails updated: ${r.data.updated}/${r.data.scanned}${r.data.failed ? ` (${r.data.failed} failed)` : ""}`);
                      load();
                    } catch (err) {
                      toast.error(formatApiError(err?.response?.data?.detail) || "Backfill failed");
                    }
                  }}
                  className="rounded-full bg-transparent border-white/15 hover:bg-white/5"
                >
                  <Film size={14} className="mr-2" /> Sync Vimeo covers
                </Button>
                <Button
                  onClick={() => { if (showForm) { cancelLessonEdit(); } else { setEditingLessonId(null); setForm(blankLesson()); setShowForm(true); } }}
                  data-testid="admin-new-lesson"
                  className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                  <Plus size={14} className="mr-2" /> {showForm ? "Close" : "New lesson"}
                </Button>
              </div>
            </div>
            {showForm && (
              <form onSubmit={createLesson} data-testid="lesson-form"
                className="glass rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 text-[11px] tracking-[0.22em] uppercase text-[#FF8A00]">
                  {editingLessonId ? "Edit lesson" : "New lesson"}
                </div>
                <FormField label="Title">
                  <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required
                    data-testid="lesson-title" className="bg-[#0F0F0F] border-white/10"/>
                </FormField>
                <FormField label="Category">
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="lesson-category" className="bg-[#0F0F0F] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Tier required">
                  <Select value={form.tier_required} onValueChange={(v) => setForm({...form, tier_required: v})}>
                    <SelectTrigger data-testid="lesson-tier" className="bg-[#0F0F0F] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="free">Free</SelectItem>
                      {["workshop", "course", "membership", "cohort"].map((key) => {
                        const t = tiers.find((x) => (x.grants_tier === key));
                        return (
                          <SelectItem key={key} value={key}>
                            {t ? `${t.name} (${t.price})` : key}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Duration (min)">
                  <Input type="number" value={form.duration_min}
                    onChange={(e) => setForm({...form, duration_min: e.target.value})}
                    className="bg-[#0F0F0F] border-white/10"/>
                </FormField>
                <FormField label="Order">
                  <Input type="number" value={form.order}
                    onChange={(e) => setForm({...form, order: e.target.value})}
                    className="bg-[#0F0F0F] border-white/10"/>
                </FormField>
                <FormField label="Drip day">
                  <Input type="number" value={form.drip_day}
                    onChange={(e) => setForm({...form, drip_day: e.target.value})}
                    className="bg-[#0F0F0F] border-white/10"/>
                </FormField>
                <FormField label="Video" span={2}>
                  <div className="space-y-4">
                    {/* Vimeo (preferred for course videos) */}
                    <div className="rounded-xl bg-[#00ADEF]/[0.04] border border-[#00ADEF]/20 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] tracking-[0.22em] uppercase text-[#00ADEF] font-semibold">Vimeo (recommended)</div>
                        <div className="text-[10px] text-white/40">Domain-locked · unlimited size</div>
                      </div>
                      <VimeoUploader
                        value={form.vimeo_video_id}
                        onUploaded={(vid, _embed, thumb) => setForm({...form, vimeo_video_id: vid, video_file_id: "", video_url: "", thumbnail_url: thumb || form.thumbnail_url})}
                        onClear={() => setForm({...form, vimeo_video_id: ""})}
                      />
                    </div>

                    <div className="relative flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-white/30">
                      <div className="flex-1 h-px bg-white/5" /> or upload direct <div className="flex-1 h-px bg-white/5" />
                    </div>

                    {/* Local object storage upload */}
                    <div className="flex flex-wrap items-center gap-3">
                      <input
                        ref={fileRef}
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v"
                        onChange={(e) => handleVideoUpload(e.target.files?.[0])}
                        className="hidden"
                        data-testid="lesson-video-file"
                      />
                      <Button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading || !!form.vimeo_video_id}
                        data-testid="lesson-upload-btn"
                        className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10 disabled:opacity-40"
                      >
                        <Upload size={14} className="mr-2" />
                        {uploading ? `Uploading ${uploadPct}%` : "Upload to our storage (max 500 MB)"}
                      </Button>
                      {form.video_file_id && (
                        <div className="flex items-center gap-2 text-xs text-[#FF8A00]">
                          <CheckCircle size={14} /> Uploaded ({form.video_file_id.slice(0,8)}…)
                          <button type="button"
                            onClick={() => setForm({...form, video_file_id: ""})}
                            className="text-white/40 hover:text-white underline ml-2">clear</button>
                        </div>
                      )}
                    </div>
                    {uploading && (
                      <div className="h-1 rounded-full bg-[#0F0F0F] overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] transition-[width]"
                             style={{ width: `${uploadPct}%` }} />
                      </div>
                    )}

                    <div className="relative flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-white/30">
                      <div className="flex-1 h-px bg-white/5" /> or paste embed URL <div className="flex-1 h-px bg-white/5" />
                    </div>
                    <Input value={form.video_url}
                      onChange={(e) => setForm({...form, video_url: e.target.value})}
                      placeholder="https://player.vimeo.com/video/… or any embed URL"
                      disabled={!!form.video_file_id || !!form.vimeo_video_id}
                      className="bg-[#0F0F0F] border-white/10 disabled:opacity-40"/>
                  </div>
                </FormField>
                <FormField label="Cover image (thumbnail)" span={2}>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Input
                        value={form.thumbnail_url}
                        onChange={(e) => setForm({...form, thumbnail_url: e.target.value})}
                        placeholder="https://i.vimeocdn.com/video/…  or any image URL"
                        data-testid="lesson-thumbnail-url"
                        className="bg-[#0F0F0F] border-white/10 flex-1 min-w-[240px]"
                      />
                      {form.vimeo_video_id ? (
                        <Button
                          type="button"
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/admin/vimeo/thumbnail/${form.vimeo_video_id}`);
                              if (data.thumbnail_url) {
                                setForm({...form, thumbnail_url: data.thumbnail_url});
                                toast.success("Cover fetched from Vimeo");
                              } else {
                                toast.warning("Vimeo hasn't generated a thumbnail yet — try again in a minute.");
                              }
                            } catch (err) {
                              toast.error(formatApiError(err?.response?.data?.detail) || "Fetch failed");
                            }
                          }}
                          data-testid="fetch-thumb-btn"
                          className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10"
                        >
                          <Film size={14} className="mr-2" /> Fetch from Vimeo
                        </Button>
                      ) : null}
                    </div>
                    {form.thumbnail_url && (
                      <div className="mt-2 rounded-xl overflow-hidden border border-white/10 bg-[#0F0F0F] max-w-[320px]">
                        <img
                          src={form.thumbnail_url}
                          alt="Cover preview"
                          data-testid="thumbnail-preview"
                          className="w-full aspect-video object-cover"
                          onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                        />
                      </div>
                    )}
                    <p className="text-[11px] text-white/40">
                      Auto-filled from Vimeo when you upload. Paste any image URL to override.
                    </p>
                  </div>
                </FormField>
                <FormField label="Description" span={2}>
                  <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                    className="bg-[#0F0F0F] border-white/10"/>
                </FormField>
                <FormField label="Key takeaways (one per line)" span={2}>
                  <Textarea value={form.key_takeaways} onChange={(e) => setForm({...form, key_takeaways: e.target.value})}
                    className="bg-[#0F0F0F] border-white/10"/>
                </FormField>
                <FormField label="Action steps (one per line)" span={2}>
                  <Textarea value={form.action_steps} onChange={(e) => setForm({...form, action_steps: e.target.value})}
                    className="bg-[#0F0F0F] border-white/10"/>
                </FormField>
                <div className="md:col-span-2 flex items-center gap-2">
                  <Button type="submit" data-testid="lesson-submit"
                    className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                    {editingLessonId ? "Save changes" : "Publish lesson"}
                  </Button>
                  {editingLessonId && (
                    <Button type="button" onClick={cancelLessonEdit}
                      variant="outline"
                      data-testid="lesson-cancel"
                      className="rounded-full bg-transparent border-white/10">
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            )}
            <div className="space-y-2">
              {lessons.map((l) => (
                <div key={l.lesson_id} data-testid={`lesson-row-${l.lesson_id}`}
                  className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-white/5 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{l.title}</div>
                    <div className="text-[11px] text-white/40 mt-0.5">
                      {l.category} · {l.tier_required} · {l.duration_min}min
                      {l.vimeo_video_id
                        ? " · Vimeo"
                        : l.video_file_id
                          ? " · uploaded video"
                          : l.video_url
                            ? " · embed URL"
                            : " · no video"}
                      {(l.resources?.length > 0) && ` · ${l.resources.length} resource${l.resources.length > 1 ? "s" : ""}`}
                    </div>
                  </div>
                  <LessonResourcesAdmin lesson={l} onChange={load} />
                  <button data-testid={`edit-lesson-${l.lesson_id}`} onClick={() => startEditLesson(l)}
                    className="text-white/40 hover:text-white p-2">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`delete-lesson-${l.lesson_id}`} onClick={() => deleteLesson(l.lesson_id)}
                    className="text-white/40 hover:text-[#E52E71] p-2">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="tiers" className="mt-6">
            <TiersAdminTab onChange={invalidateTiersCache} />
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <EventsAdminTab />
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <BookingsAdminTab />
          </TabsContent>

          <TabsContent value="subscribers" className="mt-6">
            <NewsletterAdminTab />
          </TabsContent>

          <TabsContent value="broadcast" className="mt-6">
            <BroadcastAdminTab />
          </TabsContent>

          <TabsContent value="promo" className="mt-6">
            <PromoCodesAdminTab />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <SettingsAdminTab />
          </TabsContent>

          <TabsContent value="protocol" className="mt-6">
            <ProtocolAdminTab users={users} />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div className="text-sm text-white/55">
                Grant or revoke paid access, block abusive members, or permanently delete an account. Admins are protected from self-actions.
                <div className="mt-1 text-[11px] text-white/35">
                  Showing {users.length} {users.length === 1 ? "user" : "users"} — newest first.
                </div>
              </div>
              <button
                type="button"
                onClick={load}
                data-testid="admin-users-refresh-btn"
                className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] tracking-[0.18em] uppercase border border-white/10 hover:bg-white/5 transition"
              >
                Refresh
              </button>
            </div>
            <div className="glass rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-[11px] tracking-[0.18em] uppercase text-white/40">
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Auth</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Tier / Access</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isSelf = u.email === "admin@thrive365.com";
                    const blocked = u.status === "blocked";
                    return (
                    <tr key={u.user_id} className={`border-t border-white/5 ${blocked ? "opacity-60" : ""}`}
                        data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-3">
                        <div>{u.name}</div>
                        {u.is_admin && <span className="text-[9px] tracking-[0.22em] uppercase text-[#E52E71]">admin</span>}
                      </td>
                      <td className="px-4 py-3 text-white/60">{u.email}</td>
                      <td className="px-4 py-3 text-white/60">{u.auth_provider}</td>
                      <td className="px-4 py-3">
                        {blocked ? (
                          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#E52E71]">
                            <Ban size={11} /> Blocked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#FF8A00]">
                            <ShieldCheck size={11} /> Active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Select value={u.tier} onValueChange={(v) => setUserTier(u.user_id, v)}
                                disabled={blocked || u.is_admin}>
                          <SelectTrigger
                            data-testid={`tier-select-${u.user_id}`}
                            className="bg-[#0F0F0F] border-white/10 w-44 h-9 text-xs tracking-[0.15em] uppercase disabled:opacity-40"
                            style={{ color: "#FF8A00" }}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="workshop">Workshop ($147)</SelectItem>
                            <SelectItem value="course">Course ($247)</SelectItem>
                            <SelectItem value="membership">Membership ($67/mo)</SelectItem>
                            <SelectItem value="cohort">Cohort ($1,997)</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button data-testid={`request-intake-${u.user_id}`}
                          onClick={() => setIntakeRequestFor(u)}
                          disabled={blocked}
                          title="Send intake request"
                          className="text-white/40 hover:text-[#FF8A00] disabled:opacity-20 disabled:cursor-not-allowed p-2">
                          <Mail size={14} />
                        </button>
                        <button data-testid={`block-user-${u.user_id}`}
                          onClick={() => toggleUserBlock(u)}
                          disabled={isSelf || u.is_admin}
                          title={blocked ? "Unblock" : "Block"}
                          className="text-white/40 hover:text-[#FF8A00] disabled:opacity-20 disabled:cursor-not-allowed p-2">
                          {blocked ? <ShieldCheck size={14} /> : <Ban size={14} />}
                        </button>
                        <button data-testid={`delete-user-${u.user_id}`}
                          onClick={() => deleteUser(u)}
                          disabled={isSelf || u.is_admin}
                          title="Delete"
                          className="text-white/40 hover:text-[#E52E71] disabled:opacity-20 disabled:cursor-not-allowed p-2">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <RequestIntakeModal
        open={!!intakeRequestFor}
        onOpenChange={(v) => { if (!v) setIntakeRequestFor(null); }}
        user={intakeRequestFor}
      />
    </AppShell>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-2xl p-5">
      <Icon size={16} className="text-[#FF8A00] mb-3" />
      <div className="font-display text-3xl">{value}</div>
      <div className="text-[11px] tracking-[0.18em] uppercase text-white/40 mt-1">{label}</div>
    </div>
  );
}

function FormField({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <Label className="text-xs text-white/60">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function blankLesson() {
  return {
    title: "", category: "peptides", tier_required: "workshop",
    description: "", video_url: "", video_file_id: "", vimeo_video_id: "",
    thumbnail_url: "",
    duration_min: 15, order: 100, drip_day: 0,
    key_takeaways: "", action_steps: "",
  };
}
